import { useState, useRef, useEffect } from "react";

//* INFO: 예약 모달, 예약 수정 모달에서 사용되는 훅입니다.
const useDraggableResizableModal = (
  initialPosition = { x: window.innerWidth - 460, y: 88 },
  initialHeight = window.innerHeight - 88 - 20,
  minWidth = 440,
  minHeight = 300
) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [modalPosition, setModalPosition] = useState(initialPosition);
  const [modalHeight, setModalHeight] = useState(initialHeight);

  const dragStartPosition = useRef({ x: 0, y: 0 });

  //* 드래그 시작
  const startDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    document.body.style.userSelect = "none";
    setIsDragging(true);
    dragStartPosition.current = {
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y,
    };
  };

  //* 드래그 중 모달 위치 변경
  const handleDrag = (e: MouseEvent) => {
    if (!isDragging) return;

    let newX = e.clientX - dragStartPosition.current.x;
    let newY = e.clientY - dragStartPosition.current.y;

    //* 가로, 세로 제한
    newX = Math.max(60, Math.min(newX, window.innerWidth - minWidth));
    newY = Math.max(0, Math.min(newY, window.innerHeight - modalHeight));

    if (e.clientY < 64) {
      newY = 64;
    } else if (e.clientY > window.innerHeight - modalHeight) {
      if (window.innerHeight - e.clientY < minHeight) return;
      setModalHeight(window.innerHeight - e.clientY);
      setModalPosition({ x: newX, y: e.clientY - dragStartPosition.current.y });
      return;
    }
    setModalPosition({ x: newX, y: newY });
  };

  //* 드래그 종료
  const endDrag = () => {
    document.body.style.userSelect = "auto";
    setIsDragging(false);
  };

  //* 리사이징 시작
  const startResize = (e: React.MouseEvent<HTMLDivElement>) => {
    document.body.style.userSelect = "none";
    setIsResizing(true);
    dragStartPosition.current.y = e.clientY - modalHeight;
  };

  //* 리사이징 중 모달 높이 변경
  const handleResize = (e: MouseEvent) => {
    if (!isResizing) return;
    const newHeight = e.clientY - dragStartPosition.current.y;

    if (newHeight < minHeight) {
      setModalHeight(minHeight);
    } else if (newHeight > window.innerHeight - 88 - 20) {
      setModalHeight(window.innerHeight - 88 - 20);
    } else setModalHeight(newHeight);
  };

  //* 리사이징 종료
  const endResize = () => {
    document.body.style.userSelect = "auto";
    setIsResizing(false);
  };

  //* 드래그 및 리사이즈 이벤트 핸들러 등록
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleDrag);
      document.addEventListener("mouseup", endDrag);
    } else {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("mouseup", endDrag);
    }

    if (isResizing) {
      document.addEventListener("mousemove", handleResize);
      document.addEventListener("mouseup", endResize);
    } else {
      document.removeEventListener("mousemove", handleResize);
      document.removeEventListener("mouseup", endResize);
    }

    return () => {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("mousemove", handleResize);
      document.removeEventListener("mouseup", endResize);
    };
  }, [isDragging, isResizing]);

  return {
    modalPosition,
    modalHeight,
    startDrag,
    startResize,
    setModalHeight,
    setModalPosition,
  };
};

export default useDraggableResizableModal;
